import type { Node, NodeType } from '@tiptap/pm/model';
import type { EditorState } from '@tiptap/pm/state';

/**
 * Finds a custom node in the document by attribute.
 *
 * @param doc - The document to search in.
 * @param nodeType - The type of node to search for.
 * @param attributeName - The name of the attribute to match.
 * @param attributeValue - The value of the attribute to match.
 * @returns The found node and its position, or null if not found.
 */
export function findCustomNodeByAttribute(
  doc: EditorState['doc'],
  nodeType: NodeType,
  attributeName: string,
  attributeValue: string
) {
  let foundNode = null;

  doc.descendants((node, pos) => {
    if (node.type === nodeType && node.attrs[attributeName] === attributeValue) {
      foundNode = { node, pos };
      return false;
    }
  });

  return foundNode as { node: Node; pos: number } | null;
}

/**
 * Checks if a given node is of a specific type.
 *
 * @param node - The node to check.
 * @param type - The type to check against.
 * @returns `true` if the node is of the specified type, otherwise `false`.
 */
export function isNodeOfType(type: string | string[]) {
  return (node: Node) => {
    if (Array.isArray(type)) return type.includes(node.type.name);
    return node.type.name === type;
  };
}

/**
 * Checks if a given node is a list wrapper.
 *
 * This function determines whether the provided node is of type 'bulletList',
 * 'orderedList', or 'taskList'.
 *
 * @param node - The node to check.
 * @returns `true` if the node is a list wrapper, otherwise `false`.
 */
export const isListWrapper = (node: Node) => isNodeOfType(['bulletList', 'orderedList', 'taskList'])(node);

/**
 * Checks if a given node is of type 'listItem'.
 *
 * @param node - The node to check.
 * @returns True if the node is of type 'listItem', otherwise false.
 */
export const isListItem = (node: Node) => isNodeOfType('listItem')(node);
